<template>
    <div>
        <div style="height: 260px">
            <Bar :data="chartData" :options="chartOptions"/>
        </div>
        <div class="d-flex flex-wrap ga-2 mt-3">
            <v-chip v-for="level in USAGE_LEVELS" :key="level.key" size="small" variant="outlined">
                <span class="legend-dot mr-2" :style="{background: level.color}"/>
                {{ level.label }}
            </v-chip>
        </div>
    </div>
</template>

<script setup>
import {computed} from 'vue'
import {Bar} from 'vue-chartjs'
import {Chart, registerables} from 'chart.js'
import {USAGE_LEVELS, usageLevel, formatMinutes} from './appInstallFormat'

Chart.register(...registerables)

const props = defineProps({
    // [{date: 'YYYY-MM-DD', opens, foreground_seconds}], one per day of the month.
    days: {type: Array, required: true},
})

// Unused days get a short grey stub so every day stays visible.
const STUB_MINUTES = 0.3

const chartData = computed(() => ({
    labels: props.days.map((d) => Number(d.date.slice(8, 10))),
    datasets: [{
        label: 'Minutes in the app',
        data: props.days.map((d) => (usageLevel(d).key === 'none' ? STUB_MINUTES : Math.max(d.foreground_seconds / 60, STUB_MINUTES))),
        backgroundColor: props.days.map((d) => usageLevel(d).color),
        borderRadius: 3,
        maxBarThickness: 22,
    }],
}))

// Parse YYYY-MM-DD as a local date so the label does not shift a day.
const dayTitle = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})
}

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {display: false},
        tooltip: {
            callbacks: {
                title: (items) => dayTitle(props.days[items[0].dataIndex].date),
                label: (item) => {
                    const day = props.days[item.dataIndex]
                    if (usageLevel(day).key === 'none') return 'Not opened'
                    const opens = `${day.opens} open${day.opens === 1 ? '' : 's'}`
                    return `${formatMinutes(day.foreground_seconds)} in the app, ${opens}`
                },
            },
        },
    },
    scales: {
        x: {grid: {display: false}, title: {display: true, text: 'Day of month'}},
        y: {beginAtZero: true, title: {display: true, text: 'Minutes'}, ticks: {precision: 0}},
    },
}))
</script>

<style scoped>
.legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
}
</style>
